/**
 * LetterPreviewPage — preview and edit a letter before generating the PDF.
 * Accessed at /executor/:clientId/letters/:instanceKey
 */

import { useState, useEffect, useId } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, RefreshCw, Edit2, Check, Info } from "lucide-react";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface LetterInstance {
  instanceKey: string;
  templateId: string;
  templateType: string;
  templateName: string;
  category: string;
  displayName: string;
  institutionName?: string;
  accountIdentifier?: string;
  mergedData: Record<string, string>;
  executorFields: Array<{
    key: string;
    label: string;
    type: string;
    options?: string[];
    required: boolean;
    defaultValue?: string;
  }>;
  generatedLetterId?: string;
  status: "not_generated" | "draft" | "generated" | "downloaded";
  generatedAt?: string | null;
  downloadedAt?: string | null;
  pdfUrl?: string | null;
  submissionInfo?: {
    formNumber?: string;
    submitMethod: string;
    submitTo?: string;
    submitToByProvince?: Record<string, string>;
    requiredDocuments?: string[];
    notes?: string;
  };
  clientProvince?: string;
}

interface Props {
  clientId: string;
  instanceKey: string; // URL-decoded instance key
}

export default function LetterPreviewPage({ clientId, instanceKey }: Props) {
  const uid = useId();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const { data: letters = [], isLoading } = useQuery<LetterInstance[]>({
    queryKey: ["/api/clients", clientId, "letters"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/letters`);
      if (!res.ok) throw new Error("Failed to fetch letters");
      return res.json();
    },
    enabled: !!clientId,
  });

  const letter = letters.find(l => l.instanceKey === instanceKey);

  // Executor-provided field values
  const [executorEdits, setExecutorEdits] = useState<Record<string, string>>({});
  const [downloading, setDownloading] = useState(false);

  // Load saved executor profile to pre-fill personal fields
  const { data: profile } = useQuery<{
    name: string;
    relationship?: string | null;
    email: string;
    phone?: string | null;
    executorAddress?: string | null;
    deathCertificateNumber?: string | null;
  }>({
    queryKey: ["/api/clients", clientId, "my-executor-profile"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/my-executor-profile`);
      if (!res.ok) throw new Error("No profile");
      return res.json();
    },
    enabled: !!clientId,
    retry: false,
  });

  useEffect(() => {
    if (letter) {
      // Pre-fill today for letter_date and executor profile fields
      const today = new Date().toISOString().slice(0, 10);
      const defaults: Record<string, string> = {};
      for (const f of letter.executorFields) {
        if (f.defaultValue === "today") defaults[f.key] = today;
      }
      if (profile) {
        if (profile.name) defaults["executor_name"] = profile.name;
        if (profile.relationship) defaults["executor_relationship"] = profile.relationship;
        if (profile.email) defaults["executor_email"] = profile.email;
        if (profile.phone) defaults["executor_phone"] = profile.phone;
        if (profile.executorAddress) defaults["executor_address"] = profile.executorAddress;
        if (profile.deathCertificateNumber) defaults["death_certificate_number"] = profile.deathCertificateNumber;
      }
      setExecutorEdits(prev => ({ ...defaults, ...prev }));
    }
  }, [letter?.instanceKey, profile]);

  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!letter) throw new Error("Letter not found");
      const res = await fetch(`/api/clients/${clientId}/letters/generate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          instanceKey: letter.instanceKey,
          templateId: letter.templateId,
          displayName: letter.displayName,
          institutionName: letter.institutionName,
          accountIdentifier: letter.accountIdentifier,
          mergedData: letter.mergedData,
          executorEdits,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Generation failed");
      }
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: "Letter generated", description: "Your PDF is ready to download." });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "letters"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to generate", description: err.message, variant: "destructive" });
    },
  });

  const handleDownload = async () => {
    const gen = generateMutation.data as any;
    const letterId = gen?.id ?? letter?.generatedLetterId;
    if (!letterId) return;

    setDownloading(true);
    try {
      const res = await fetch(`/api/clients/${clientId}/letters/${letterId}/download`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(letter?.displayName ?? "letter").replace(/[^a-z0-9]/gi, "-").toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "letters"] });
    } catch (err: any) {
      toast({ title: "Download failed", description: err.message, variant: "destructive" });
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!letter) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Button variant="ghost" onClick={() => setLocation(`/executor/${clientId}/letters`)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Letters
        </Button>
        <p className="text-muted-foreground mt-4">Letter not found.</p>
      </div>
    );
  }

  const isGenerated = generateMutation.isSuccess || !!letter.generatedLetterId;
  const generatedLetterId = (generateMutation.data as any)?.id ?? letter.generatedLetterId;

  // Build preview text by merging all data
  const previewData = {
    ...letter.mergedData,
    ...executorEdits,
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setLocation(`/executor/${clientId}/letters`)}
          className="shrink-0"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold truncate">{letter.displayName}</h1>
          {letter.accountIdentifier && (
            <p className="text-xs text-muted-foreground">{letter.accountIdentifier}</p>
          )}
        </div>
        {isGenerated && (
          <Badge className="shrink-0 bg-green-50 text-green-700 border-green-200">
            <Check className="w-3 h-3 mr-1" />
            Generated
          </Badge>
        )}
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-blue-100 border border-blue-300" />
          Auto-filled from estate plan
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-amber-100 border border-amber-300" />
          You provide
        </span>
      </div>

      {/* Executor fields */}
      {letter.executorFields.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Edit2 className="w-4 h-4 text-amber-600" />
              Information you provide
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {letter.executorFields.map(field => (
              <div key={field.key} className="space-y-1">
                <Label className="text-xs text-muted-foreground" htmlFor={`${uid}-exec-${field.key}`}>
                  {field.label}
                  {field.required && <span className="text-destructive ml-1">*</span>}
                </Label>
                {field.type === "select" && field.options ? (
                  <select
                    value={executorEdits[field.key] ?? ""}
                    onChange={e => setExecutorEdits(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full h-9 rounded-md border border-input bg-amber-50 px-3 text-sm"
                  >
                    <option value="">Select…</option>
                    {field.options.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : field.type === "textarea" ? (
                  <Textarea id={`${uid}-exec-${field.key}`}
                    value={executorEdits[field.key] ?? ""}
                    onChange={e => setExecutorEdits(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="bg-amber-50 text-sm"
                    rows={3}
                  />
                ) : field.type === "date" ? (
                  <Input
                    type="date"
                    value={executorEdits[field.key] ?? ""}
                    onChange={e => setExecutorEdits(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="bg-amber-50 h-9 text-sm"
                  />
                ) : (
                  <Input
                    value={executorEdits[field.key] ?? ""}
                    onChange={e => setExecutorEdits(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="bg-amber-50 h-9 text-sm"
                  />
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Auto-filled preview */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-sm bg-blue-100 border border-blue-300" />
            Auto-filled from estate plan
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(previewData)
              .filter(([k]) => !k.startsWith("letter_date") && !k.startsWith("executor_") && k !== "account_details")
              .map(([key, val]) => (
                <div key={key} className="bg-blue-50 rounded-md p-2 border border-blue-100">
                  <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, " ")}</p>
                  <p className="text-xs font-medium truncate">{val}</p>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Letter preview */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Letter Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed bg-muted/30 rounded-lg p-4 max-h-96 overflow-y-auto border">
            {renderPreviewBody(letter.mergedData, executorEdits)}
          </pre>
        </CardContent>
      </Card>

      {/* Submission info box */}
              {letter.submissionInfo && (
                <div className="mt-6 border rounded-lg p-4 bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="w-4 h-4 text-blue-600 shrink-0" />
                    <p className="font-semibold text-blue-900 text-sm">Submission Instructions</p>
                  </div>
                  <div className="space-y-2 text-sm text-blue-900">
                    {letter.submissionInfo.formNumber && (
                      <p><span className="font-medium">Form:</span> {letter.submissionInfo.formNumber}</p>
                    )}
                    <p><span className="font-medium">Send by:</span> {letter.submissionInfo.submitMethod}</p>

                    {letter.submissionInfo.submitToByProvince && (() => {
                      const province = letter.clientProvince ?? "";
                      const dest = letter.submissionInfo.submitToByProvince![province];
                      if (dest) {
                        const label = letter.submissionInfo.submitMethod === "fax" ? "Fax number" : "Send to";
                        return <p key="dest"><span className="font-medium">{label} ({province}):</span> {dest}</p>;
                      }
                      return (
                        <div key="dest-all">
                          <p className="font-medium">Send to (by province):</p>
                          <ul className="ml-4 mt-1 space-y-0.5">
                            {Object.entries(letter.submissionInfo.submitToByProvince!).map(([prov, d]) => (
                              <li key={prov}>{prov}: {d}</li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}

                    {letter.submissionInfo.submitTo && !letter.submissionInfo.submitToByProvince && (
                      <p><span className="font-medium">Send to:</span> {letter.submissionInfo.submitTo}</p>
                    )}

                    {letter.submissionInfo.requiredDocuments && letter.submissionInfo.requiredDocuments.length > 0 && (
                      <div>
                        <p className="font-medium">Include with submission:</p>
                        <ul className="ml-4 mt-1 list-disc space-y-0.5">
                          {letter.submissionInfo.requiredDocuments.map((doc, i) => (
                            <li key={i}>{doc}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {letter.submissionInfo.notes && (
                      <p className="text-blue-700 italic">{letter.submissionInfo.notes}</p>
                    )}
                  </div>
                </div>
              )}

      {/* Action buttons */}
      <div className="flex gap-3 pt-1">
        <Button
          className="flex-1"
          onClick={() => generateMutation.mutate()}
          disabled={generateMutation.isPending}
        >
          {generateMutation.isPending ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Generating…</>
          ) : isGenerated ? (
            <><RefreshCw className="w-4 h-4 mr-2" />Regenerate PDF</>
          ) : (
            "Generate PDF"
          )}
        </Button>
        {isGenerated && generatedLetterId && (
          <Button
            variant="outline"
            onClick={handleDownload}
            disabled={downloading}
            className="flex-1"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? "Downloading…" : "Download PDF"}
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground text-center pb-2">
        Present this letter with the original or certified death certificate and your executor identification.
      </p>
    </div>
  );
}

function renderPreviewBody(
  mergedData: Record<string, string>,
  executorEdits: Record<string, string>
): string {
  // This is a simplified client-side preview — the real rendering happens server-side
  const data = { ...mergedData, ...executorEdits };

  // Show key fields in a readable format
  const lines: string[] = [];
  lines.push(`Date: ${data.letter_date ?? new Date().toLocaleDateString()}`);
  lines.push("");
  lines.push(`From: ${data.executor_name ?? "[Your name]"}`);
  if (data.executor_address) lines.push(data.executor_address);
  lines.push("");
  lines.push(`Re: Estate of ${data.deceased_name ?? "[Deceased name]"}`);
  if (data.institution_name && data.institution_name !== "[Institution name]") {
    lines.push(`To: ${data.institution_name}`);
  }
  lines.push("");
  lines.push("--- LETTER CONTENT ---");
  lines.push("");
  lines.push(`This letter formally notifies ${data.institution_name ?? "the recipient"} of the passing of ${data.deceased_name ?? "[deceased]"} on ${data.date_of_death ?? "[date of death]"}.`);
  lines.push("");
  if (data.account_details) {
    lines.push("Accounts on file:");
    lines.push(data.account_details);
    lines.push("");
  }
  if (data.account_identifier && data.account_identifier !== "[Account number]") {
    lines.push(`Account / Policy: ${data.account_identifier}`);
    lines.push("");
  }
  lines.push("[Full letter text will appear in the generated PDF]");
  lines.push("");
  lines.push(`Sincerely,`);
  lines.push(data.executor_name ?? "[Your name]");
  lines.push(`Executor, Estate of ${data.deceased_name ?? "[Deceased]"}`);

  return lines.join("\n");
}
